import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { App, ConfigProvider, theme } from 'antd';
import frFR from 'antd/locale/fr_FR';
import { useLocale } from '../common/i18n';

const THEME_KEY = 'theme-mode';
const VALID_MODES = ['light', 'dark', 'system'];

const ThemeCtx = createContext({
    mode: 'light',         // preference utilisateur : light | dark | system
    effective: 'light',     // valeur résolue (système → light ou dark)
    setMode: () => {},
    toggle: () => {},
});

export const useAppTheme = () => useContext(ThemeCtx);

const getSystemTheme = () =>
    (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
        ? 'dark' : 'light';

const readStoredMode = () => {
    try {
        const v = localStorage.getItem(THEME_KEY);
        return VALID_MODES.includes(v) ? v : 'light';
    } catch { return 'light'; }
};

export const AppThemeProvider = ({ children }) => {
    const [mode, setModeState] = useState(readStoredMode);
    const [systemTheme, setSystemTheme] = useState(getSystemTheme);
    const { antdLocale } = useLocale();

    // Écoute changement OS (utilisée si mode === 'system')
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (e) => setSystemTheme(e.matches ? 'dark' : 'light');
        mql.addEventListener?.('change', onChange);
        return () => mql.removeEventListener?.('change', onChange);
    }, []);

    const effective = mode === 'system' ? systemTheme : mode;
    const isDark = effective === 'dark';

    // Persistance préférence
    useEffect(() => {
        try { localStorage.setItem(THEME_KEY, mode); } catch { /* */ }
    }, [mode]);

    // Propage [data-theme] sur <html> pour les CSS custom properties
    useEffect(() => {
        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-theme', effective);
        }
    }, [effective]);

    const setMode = useCallback((next) => {
        if (VALID_MODES.includes(next)) setModeState(next);
    }, []);

    const toggle = useCallback(() => {
        // light → dark → system → light
        setModeState((m) => {
            if (m === 'light') return 'dark';
            if (m === 'dark') return 'system';
            return 'light';
        });
    }, []);

    const value = useMemo(() => ({ mode, effective, setMode, toggle }), [mode, effective, setMode, toggle]);

    const requiredMarkAfterLabel = (labelNode, { required }) => (
        <>
            {labelNode}
            {required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
        </>
    );

    return (
        <ThemeCtx.Provider value={value}>
            <ConfigProvider
                locale={antdLocale || frFR}
                form={{ requiredMark: requiredMarkAfterLabel }}
                theme={{
                    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
                    token: {
                        colorPrimary: '#1A2B5F',
                        colorLink: '#1A2B5F',
                        borderRadiusLG: 12,
                    },
                    components: {
                        Layout: {
                            headerBg: isDark ? '#1f1f1f' : '#fff',
                            siderBg: isDark ? '#1f1f1f' : '#fff',
                            bodyBg: isDark ? '#141414' : '#F5F7FA',
                        },
                        Menu: {
                            itemHeight: 44,
                            itemSelectedBg: '#1A2B5F',
                            itemSelectedColor: '#fff',
                            itemHoverBg: isDark ? '#2a2a2a' : '#eef1f8',
                        },
                        Button: {
                            colorPrimary: '#C8102E',
                            colorPrimaryHover: '#a50d24',
                            colorPrimaryActive: '#8c0b1f',
                        },
                    },
                }}
            >
                <App>{children}</App>
            </ConfigProvider>
        </ThemeCtx.Provider>
    );
};
